/**
 * Catalog of pool-analysis fee estimate methods.
 * Single source of truth for method metadata, data requirements, and active-method switching.
 */

export type FeeEstimateMethodTier = "headline" | "comparison" | "legacy";

export type FeeEstimateDataRequirement =
	| "catalogApr1d"
	| "catalogApr7d"
	| "catalogApr30d"
	| "depositRange"
	| "onChainTokenOrder"
	| "tokenUsdPrices"
	| "chart1dFees"
	| "chartPeriodFees"
	| "tickLiquidity"
	| "livePoolLiquidity"
	| "swapHistory";

export type FeeEstimateMethodId =
	| "tableApr1d"
	| "tableApr7d"
	| "tableApr30d"
	| "tableApr1dRangeAdjusted"
	| "tableApr30dRangeAdjusted"
	| "rangeAdjusted24hReplay"
	| "legacyLiquidityShareSelectedAvg"
	| "legacyLiquidityShareVolTier"
	| "legacyLiquidityShareSelectedTotal"
	| "legacyLiquidityShareLatestBucket"
	| "legacyLiquidityShareTrailing24h";

export interface FeeEstimateMethodDefinition {
	id: FeeEstimateMethodId;
	label: string;
	shortNote: string;
	tier: FeeEstimateMethodTier;
	requirements: readonly FeeEstimateDataRequirement[];
	isActiveDefault?: boolean;
}

/** Product default headline method (override via env or debug). */
export const ACTIVE_FEE_ESTIMATE_METHOD_ID: FeeEstimateMethodId = "tableApr1dRangeAdjusted";

const REPLAY_BASE: readonly FeeEstimateDataRequirement[] = [
	"catalogApr1d",
	"depositRange",
	"onChainTokenOrder",
	"tokenUsdPrices",
	"chart1dFees",
	"tickLiquidity",
	"livePoolLiquidity",
	"swapHistory",
];

const LEGACY_LIQUIDITY_SHARE: readonly FeeEstimateDataRequirement[] = [
	"chartPeriodFees",
	"tickLiquidity",
	"livePoolLiquidity",
	"depositRange",
];

export const FEE_ESTIMATE_METHOD_CATALOG: Record<FeeEstimateMethodId, FeeEstimateMethodDefinition> =
	{
		tableApr1d: {
			id: "tableApr1d",
			label: "Table APR (1D) — raw",
			shortNote:
				"Catalog APR (1D); deposit × APR ÷ 365. Matches pools table — pool-average, not range-adjusted.",
			tier: "comparison",
			requirements: ["catalogApr1d"],
		},
		tableApr7d: {
			id: "tableApr7d",
			label: "Table APR (7D)",
			shortNote: "Same as pools table APR (7D), applied to deposit ÷ 365.",
			tier: "comparison",
			requirements: ["catalogApr7d"],
		},
		tableApr30d: {
			id: "tableApr30d",
			label: "Table APR (30D)",
			shortNote: "Same as pools table APR (30D), applied to deposit ÷ 365.",
			tier: "comparison",
			requirements: ["catalogApr30d"],
		},
		tableApr1dRangeAdjusted: {
			id: "tableApr1dRangeAdjusted",
			label: "Table APR (1D) + range",
			shortNote:
				"deposit × table APR (1D) ÷ 365, scaled by L_user(your range) / L_user(pair default band); $0 when current price is out of range.",
			tier: "headline",
			requirements: ["catalogApr1d", "depositRange", "onChainTokenOrder", "tokenUsdPrices"],
			isActiveDefault: true,
		},
		tableApr30dRangeAdjusted: {
			id: "tableApr30dRangeAdjusted",
			label: "Table APR (30D) + range",
			shortNote:
				"Same as 1D + range hybrid but uses pools table APR (30D); Pro default when no env/debug override.",
			tier: "comparison",
			requirements: ["catalogApr30d", "depositRange", "onChainTokenOrder", "tokenUsdPrices"],
		},
		rangeAdjusted24hReplay: {
			id: "rangeAdjusted24hReplay",
			label: "Range-adjusted (24h replay)",
			shortNote:
				"Σ feeᵢ × L_user/(L_active+L_user) over 24h chart/swap events; tick-accurate when subgraph data exists.",
			tier: "comparison",
			requirements: REPLAY_BASE,
		},
		legacyLiquidityShareSelectedAvg: {
			id: "legacyLiquidityShareSelectedAvg",
			label: "Liquidity share — selected avg/day",
			shortNote: "Chart fees ÷ selected period; share L/(L+pool).",
			tier: "legacy",
			requirements: LEGACY_LIQUIDITY_SHARE,
		},
		legacyLiquidityShareVolTier: {
			id: "legacyLiquidityShareVolTier",
			label: "Liquidity share — vol × tier avg/day",
			shortNote: "Volume × fee tier when indexed fees look off vs tier.",
			tier: "legacy",
			requirements: LEGACY_LIQUIDITY_SHARE,
		},
		legacyLiquidityShareSelectedTotal: {
			id: "legacyLiquidityShareSelectedTotal",
			label: "Liquidity share — selected total (not avg)",
			shortNote: "Loaded fee points total — for comparison only.",
			tier: "legacy",
			requirements: LEGACY_LIQUIDITY_SHARE,
		},
		legacyLiquidityShareLatestBucket: {
			id: "legacyLiquidityShareLatestBucket",
			label: "Liquidity share — latest bucket",
			shortNote: "Last loaded fee point.",
			tier: "legacy",
			requirements: LEGACY_LIQUIDITY_SHARE,
		},
		legacyLiquidityShareTrailing24h: {
			id: "legacyLiquidityShareTrailing24h",
			label: "Liquidity share — loaded trailing 24h",
			shortNote: "Sum of fee points in last 24h of loaded series.",
			tier: "legacy",
			requirements: ["chart1dFees", "tickLiquidity", "livePoolLiquidity", "depositRange"],
		},
	};

export const FEE_ESTIMATE_METHOD_IDS = Object.keys(
	FEE_ESTIMATE_METHOD_CATALOG,
) as FeeEstimateMethodId[];

export const COMPARISON_FEE_METHOD_IDS = FEE_ESTIMATE_METHOD_IDS.filter(
	(id) => FEE_ESTIMATE_METHOD_CATALOG[id].tier === "comparison",
);

export const LEGACY_FEE_METHOD_IDS = FEE_ESTIMATE_METHOD_IDS.filter(
	(id) => FEE_ESTIMATE_METHOD_CATALOG[id].tier === "legacy",
);

export const DEBUG_COMPARISON_METHOD_IDS: FeeEstimateMethodId[] = [
	...COMPARISON_FEE_METHOD_IDS,
	...LEGACY_FEE_METHOD_IDS,
];

/** @deprecated Use FEE_ESTIMATE_METHOD_CATALOG.tableApr1dRangeAdjusted */
export const HEADLINE_FEE_METHOD_ID = ACTIVE_FEE_ESTIMATE_METHOD_ID;

/** @deprecated Use FEE_ESTIMATE_METHOD_CATALOG */
export const HEADLINE_FEE_METHOD = FEE_ESTIMATE_METHOD_CATALOG.tableApr1dRangeAdjusted;

/** @deprecated Use FEE_ESTIMATE_METHOD_CATALOG */
export const RANGE_ADJUSTED_REPLAY_METHOD = FEE_ESTIMATE_METHOD_CATALOG.rangeAdjusted24hReplay;

/** @deprecated Use FEE_ESTIMATE_METHOD_CATALOG entries for tableApr1d/7d/30d */
export const TABLE_APR_FEE_METHODS = [
	FEE_ESTIMATE_METHOD_CATALOG.tableApr1d,
	FEE_ESTIMATE_METHOD_CATALOG.tableApr7d,
	FEE_ESTIMATE_METHOD_CATALOG.tableApr30d,
] as const;

const VALID_METHOD_IDS = new Set<string>(FEE_ESTIMATE_METHOD_IDS);

export function isFeeEstimateMethodId(value: string): value is FeeEstimateMethodId {
	return VALID_METHOD_IDS.has(value);
}

export function getFeeEstimateMethod(id: FeeEstimateMethodId): FeeEstimateMethodDefinition {
	return FEE_ESTIMATE_METHOD_CATALOG[id];
}

export function unionMethodRequirements(
	methodIds: readonly FeeEstimateMethodId[],
): Set<FeeEstimateDataRequirement> {
	const set = new Set<FeeEstimateDataRequirement>();
	for (const id of methodIds) {
		for (const req of FEE_ESTIMATE_METHOD_CATALOG[id].requirements) {
			set.add(req);
		}
	}
	return set;
}

export function requirementsNeedChart1d(
	requirements: ReadonlySet<FeeEstimateDataRequirement>,
): boolean {
	return requirements.has("chart1dFees");
}

export function requirementsNeedSwaps(
	requirements: ReadonlySet<FeeEstimateDataRequirement>,
): boolean {
	return requirements.has("swapHistory");
}

export function requirementsNeedOnChainTokenOrder(
	requirements: ReadonlySet<FeeEstimateDataRequirement>,
): boolean {
	return requirements.has("onChainTokenOrder");
}

export function requirementsNeedReplayCompute(methodIds: readonly FeeEstimateMethodId[]): boolean {
	return methodIds.includes("rangeAdjusted24hReplay");
}

export function getMethodsToCompute(
	activeMethodId: FeeEstimateMethodId,
	includeDebugComparisons: boolean,
): FeeEstimateMethodId[] {
	if (!includeDebugComparisons) {
		return [activeMethodId];
	}
	const ids = new Set<FeeEstimateMethodId>([activeMethodId, ...DEBUG_COMPARISON_METHOD_IDS]);
	return FEE_ESTIMATE_METHOD_IDS.filter((id) => ids.has(id));
}

export function resolveActiveFeeMethodId(
	debugOverride?: FeeEstimateMethodId | null,
	/** When set (e.g. Pro tier), used after env; before `ACTIVE_FEE_ESTIMATE_METHOD_ID`. */
	membershipTierDefault?: FeeEstimateMethodId | null,
): FeeEstimateMethodId {
	if (debugOverride && isFeeEstimateMethodId(debugOverride)) {
		return debugOverride;
	}

	const envValue =
		typeof process !== "undefined"
			? process.env.NEXT_PUBLIC_ACTIVE_FEE_ESTIMATE_METHOD?.trim()
			: undefined;

	if (envValue && isFeeEstimateMethodId(envValue)) {
		return envValue;
	}

	if (membershipTierDefault && isFeeEstimateMethodId(membershipTierDefault)) {
		return membershipTierDefault;
	}

	return ACTIVE_FEE_ESTIMATE_METHOD_ID;
}

export function isHeadlineReadyForMethod(
	methodId: FeeEstimateMethodId,
	params: {
		depositUsd: number;
		hasApr1d: boolean;
		hasApr7d: boolean;
		hasApr30d: boolean;
		hasOnChainTokenOrder: boolean;
	},
): boolean {
	if (params.depositUsd <= 0) return false;

	switch (methodId) {
		case "tableApr1d":
			return params.hasApr1d;
		case "tableApr7d":
			return params.hasApr7d;
		case "tableApr30d":
			return params.hasApr30d;
		case "tableApr1dRangeAdjusted":
			return params.hasApr1d && params.hasOnChainTokenOrder;
		case "tableApr30dRangeAdjusted":
			return params.hasApr30d && params.hasOnChainTokenOrder;
		case "rangeAdjusted24hReplay":
			return params.hasOnChainTokenOrder;
		default:
			return params.hasOnChainTokenOrder;
	}
}

/** For public copy when headline is missing APR data (no "1D" vs "30D" in user-facing strings). */
export function isMissingCatalogAprForMethod(
	methodId: FeeEstimateMethodId,
	pool: { apr1d?: string; apr7d?: string; apr30d?: string } | null | undefined,
): boolean {
	if (!pool) return true;
	const t = (s: string | undefined) => !s?.trim();
	switch (methodId) {
		case "tableApr1d":
		case "tableApr1dRangeAdjusted":
			return t(pool.apr1d);
		case "tableApr7d":
			return t(pool.apr7d);
		case "tableApr30d":
		case "tableApr30dRangeAdjusted":
			return t(pool.apr30d);
		default:
			return false;
	}
}
